import React from "react";

/**
 * Defensive top-level error boundary.
 *
 * Catches any uncaught render error in the React tree and renders a
 * minimal safe fallback. Stringifies the error reliably so a thrown
 * object can never produce a literal `[object Object]` in the UI.
 *
 * Strictly read-only — never persists the error, never sends it to a
 * remote endpoint, never echoes secret-shaped data (cookies, tokens,
 * headers). Use only for last-resort visibility.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "", name: "" };
  }

  static getDerivedStateFromError(err) {
    return {
      hasError: true,
      name: err?.name || "Error",
      message: stringifyError(err),
    };
  }

  componentDidCatch(err, info) {
    // Surface the error in the browser console for debugging, with a
    // clean stringified message. Component stack is supplied by React.
    // We deliberately do NOT POST this to a backend in this slice.
    // eslint-disable-next-line no-console
    console.error(
      "[ErrorBoundary] caught:",
      stringifyError(err),
      info?.componentStack || "(no stack)",
    );
  }

  handleRetry = () => {
    this.setState({ hasError: false, message: "", name: "" });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6"
      >
        <div className="max-w-lg w-full rounded-md border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]">
            Unexpected error
          </p>
          <h1 className="mt-2 font-display text-xl font-black text-white">
            Something didn't render right
          </h1>
          <p className="mt-3 font-mono text-xs text-white/70 break-words">
            {this.state.message || "An unknown error occurred."}
          </p>
          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              onClick={this.handleRetry}
              data-testid="error-boundary-retry"
              className="btn-primary text-xs"
            >
              Try again
            </button>
            <a
              href="/"
              className="font-mono text-[11px] text-[#00F0FF] hover:text-[#39FF14] transition-colors"
              data-testid="error-boundary-home"
            >
              Back to home
            </a>
          </div>
          <p className="mt-4 font-mono text-[10px] text-white/40">
            Open the browser console for the full error trace.
          </p>
        </div>
      </div>
    );
  }
}

function stringifyError(err) {
  if (err == null) return "Unknown error";
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message || err.name || "Error";
  // Object-shaped throws (e.g. axios rejections, plain objects) — coerce
  // without leaking large payloads.
  try {
    if (typeof err === "object") {
      // Prefer common message-bearing fields.
      if (typeof err.message === "string" && err.message) return err.message;
      if (typeof err.detail === "string" && err.detail) return err.detail;
      const s = JSON.stringify(err);
      return s.length > 400 ? `${s.slice(0, 400)}…` : s;
    }
  } catch (_) {
    /* fall through */
  }
  try {
    return String(err);
  } catch (_) {
    return "Error";
  }
}
